/**
 * modalInert — reference-counted background inerting for the canonical modal
 * system (DS2Modal) and the specialized GameplayBackdrop primitive.
 *
 * While at least one canonical modal or gameplay backdrop is active, every
 * direct child of document.body EXCEPT exempted portal roots (marked
 * data-ds2-modal-root or data-ds2-inert-exempt) is made `inert` and
 * `aria-hidden` so the application behind the backdrop is unreachable by
 * pointer, keyboard, and assistive technology. Prior attribute values are
 * saved and restored exactly when the LAST consumer releases —
 * consecutive/queued/stacked activations never briefly reactivate the
 * background.
 */

let count = 0;
const saved = new Map<Element, { inert: string | null; ariaHidden: string | null }>();

export function acquireBackgroundInert(): void {
  if (typeof document === 'undefined') return;
  if (count === 0) {
    for (const el of Array.from(document.body.children)) {
      if (el.hasAttribute('data-ds2-modal-root')) continue;
      if (el.hasAttribute('data-ds2-inert-exempt')) continue;
      if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'LINK') continue;
      saved.set(el, { inert: el.getAttribute('inert'), ariaHidden: el.getAttribute('aria-hidden') });
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
    }
  }
  count++;
}

export function releaseBackgroundInert(): void {
  if (typeof document === 'undefined') return;
  if (count === 0) return;
  count--;
  if (count === 0) {
    for (const [el, prev] of saved) {
      if (prev.inert === null) el.removeAttribute('inert');
      else el.setAttribute('inert', prev.inert);
      if (prev.ariaHidden === null) el.removeAttribute('aria-hidden');
      else el.setAttribute('aria-hidden', prev.ariaHidden);
    }
    saved.clear();
  }
}

/** Test/diagnostic helper. */
export function isBackgroundInert(): boolean {
  return count > 0;
}
